import { utils, writeFile } from 'xlsx';
import type { Node as RFNode } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import { NODE_STATUS_LABELS } from '@/types/node.types';

const NODE_TYPE_LABELS: Record<string, string> = {
  Vision: 'Vision',
  Objective: 'Objectif',
  Activity: 'Activité',
  Risk: 'Risque',
  Opportunity: 'Opportunité',
  Resource: 'Ressource',
  KPI: 'KPI',
  Stakeholder: 'Partie prenante',
};

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

function slugify(input: string): string {
  return (
    input
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'canvas'
  );
}

// ---- Table view: flat rows -----------------------------------------------

interface TableRow {
  Type: string;
  Titre: string;
  Description: string;
  Priorité: number;
  Statut: string;
  'Avancement (%)': number | '';
  'Budget projeté (€)': number | '';
  'Budget consommé (€)': number | '';
  'Effort projeté (j/h)': number | '';
  'Effort réalisé (j/h)': number | '';
  Tags: string;
  'Date de création': string;
}

export function exportTableToXLSX(
  canvasName: string,
  nodes: RFNode<NodeData>[],
): void {
  const rows: TableRow[] = nodes
    .filter((n) => n.type !== 'group')
    .map((n) => {
      const planned = toNum(n.data.budgetPlanned);
      const spent = toNum(n.data.budgetSpent);
      const effortPlanned = toNum(n.data.effortPlannedDays);
      const effortSpent = toNum(n.data.effortSpentDays);
      const pg = toNum(n.data.progress);
      return {
        Type: NODE_TYPE_LABELS[n.data.type] || n.data.type,
        Titre: n.data.title,
        Description: n.data.description ?? '',
        Priorité: n.data.priority,
        Statut: NODE_STATUS_LABELS[n.data.status] || n.data.status,
        'Avancement (%)': pg ?? '',
        'Budget projeté (€)': planned ?? '',
        'Budget consommé (€)': spent ?? '',
        'Effort projeté (j/h)': effortPlanned ?? '',
        'Effort réalisé (j/h)': effortSpent ?? '',
        Tags: (n.data.tags ?? []).join(', '),
        'Date de création': n.data.createdAt
          ? new Date(n.data.createdAt).toLocaleDateString('fr-FR')
          : '',
      };
    });

  const ws = utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 14 }, // Type
    { wch: 40 }, // Titre
    { wch: 60 }, // Description
    { wch: 8 }, // Priorité
    { wch: 12 }, // Statut
    { wch: 12 }, // Avancement
    { wch: 14 }, // Budget projeté
    { wch: 14 }, // Budget consommé
    { wch: 14 }, // Effort projeté
    { wch: 14 }, // Effort réalisé
    { wch: 20 }, // Tags
    { wch: 16 }, // Date
  ];

  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Noeuds');
  writeFile(wb, `${slugify(canvasName)}.xlsx`);
}

// ---- Tree view: hierarchical rows with level column ----------------------

export interface TreeExportItem {
  node: RFNode<NodeData>;
  children: TreeExportItem[];
}

interface TreeRow {
  Niveau: number;
  Type: string;
  Titre: string;
  Priorité: number;
  Statut: string;
  Description: string;
}

function flattenTree(items: TreeExportItem[], depth = 0, out: TreeRow[] = []): TreeRow[] {
  for (const item of items) {
    const d = item.node.data;
    const indent = '  '.repeat(depth);
    out.push({
      Niveau: depth,
      Type: NODE_TYPE_LABELS[d.type] || d.type,
      Titre: `${indent}${d.title}`,
      Priorité: d.priority,
      Statut: NODE_STATUS_LABELS[d.status] || d.status,
      Description: d.description ?? '',
    });
    if (item.children.length) flattenTree(item.children, depth + 1, out);
  }
  return out;
}

export function exportTreeToXLSX(canvasName: string, tree: TreeExportItem[]): void {
  const rows = flattenTree(tree);
  const ws = utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 8 }, // Niveau
    { wch: 12 }, // Type
    { wch: 50 }, // Titre (with indent)
    { wch: 8 }, // Priorité
    { wch: 12 }, // Statut
    { wch: 60 }, // Description
  ];

  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Arbre');
  writeFile(wb, `${slugify(canvasName)}-arbre.xlsx`);
}
